import { type GetMyExamCardQuery, useGetMyExamCard } from '@exam-room/graphql';
import { createContext, useContext, useMemo } from 'react';

import { useAuth } from '../authentication';

type ExamCard = GetMyExamCardQuery['erGetMyExamCard'];

interface ExamCardProviderProps {
  children: React.ReactNode;
}

interface ExamCardContextType {
  examCard: ExamCard;
  loading: boolean;
  error: Error | undefined;
}

const ExamCardContext = createContext<ExamCardContextType | undefined>(undefined);

function ExamCardProvider({ children }: Readonly<ExamCardProviderProps>) {
  const { authState } = useAuth();

  const {
    data,
    loading: isLoading,
    error,
  } = useGetMyExamCard({
    skip: authState !== 'LOGGED_IN',
  });

  const contextValue = useMemo(
    () => ({
      examCard: data?.erGetMyExamCard as ExamCard, // NOTE: We cast because we'll handle the undefined case in the layout
      loading: isLoading,
      error,
    }),
    [data, isLoading, error],
  );

  return <ExamCardContext.Provider value={contextValue}>{children}</ExamCardContext.Provider>;
}

function useExamCard() {
  const context = useContext(ExamCardContext);

  if (context === undefined) {
    throw new Error('useExamCard must be used within ExamCardProvider');
  }

  return context;
}

export type { ExamCardContextType, ExamCardProviderProps, ExamCard };
export { ExamCardContext, ExamCardProvider, useExamCard };
